import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Chrome, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import RichText from "../components/RichText";

export default function Login() {
  const { setUser } = useAuth();
  const { t, ts, tx } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA admin (demande du propriétaire) : step "password" → "code" (2FA actif)
  // ou "enroll" (activation EXIGÉE — avis de 30 jours écoulé le 22 oct. 2026)
  const [step, setStep] = useState("password");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [enroll, setEnroll] = useState(null);  // {qr, secret} pour l'activation forcée

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // location.state.next : retour à la page d'origine (ex. /commande) pour ne rien perdre
    const redirectUrl = window.location.origin + (location.state?.next || "/compte");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      if (data.requires_2fa) {  // admin avec 2FA actif : étape du code
        setChallenge(data.challenge_token);
        setStep("code");
        setLoading(false);
        return;
      }
      if (data.requires_2fa_setup) {  // avis de 30 jours écoulé : activation exigée
        setChallenge(data.challenge_token);
        setStep("enroll");
        try {
          const { data: s } = await api.post("/auth/2fa/setup", {},
            { headers: { Authorization: `Bearer ${data.challenge_token}` } });
          setEnroll(s);
        } catch { /* l'utilisateur pourra réessayer */ }
        setLoading(false);
        return;
      }
      setUser(data);
      navigate(location.state?.next || (data.role === "admin" || data.role === "vendeur" ? "/admin" : "/"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  // Étape du code (2FA actif) : code de l'app ou code de secours
  const verifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { code, challenge_token: challenge });
      setUser(data);
      navigate(location.state?.next || (data.role === "admin" || data.role === "vendeur" ? "/admin" : "/"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  // Activation forcée : 1er code de l'app fraîchement configurée → session ouverte
  const activate2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/activate", { code },
        { headers: { Authorization: `Bearer ${challenge}` } });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/connexion" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10">
          <h1 className="font-serif text-4xl mb-2">{t("login.title")}</h1>
          <p className="text-ivory/70 text-sm mb-8">{t("login.sub")}</p>
          {error && <p data-testid="login-error" className="text-red-400 text-sm mb-5 border border-red-400/30 bg-red-400/10 rounded-lg px-4 py-3">{error}</p>}
          {step === "password" && (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-sm text-ivory/70 mb-2">{t("login.email")}</label>
              <input
                data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-2">{t("login.password")}</label>
              <div className="relative">
                <input
                  data-testid="login-password" type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 pr-12 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
                <button type="button" data-testid="login-password-toggle" onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 hover:text-gold transition-colors duration-300">
                  {showPwd ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2.5 text-sm text-ivory/70 select-none cursor-pointer" data-testid="login-remember-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember"
                  className="w-4 h-4 accent-[#D4AF37]" />
                {t("pwd.remember")}
              </label>
              <Link to="/mot-de-passe-oublie" data-testid="login-forgot-link"
                className="text-sm text-gold hover:underline underline-offset-2">
                {t("pwd.forgot_link")}
              </Link>
            </div>
            <button
              data-testid="login-submit" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {t("login.submit")}
            </button>
          </form>
          )}

          {/* Étape 2FA — code de l'app d'authentification (ou code de secours) */}
          {step === "code" && (
            <form onSubmit={verifyCode} className="space-y-5" data-testid="login-2fa-form">
              <p className="text-sm text-ivory/75 border border-gold/25 bg-gold/10 rounded-lg px-4 py-3">
                <ShieldCheck className="inline w-4 h-4 text-gold mr-1.5 -mt-0.5" />
                <RichText text={tx("login.2fa_hint", "Compte administrateur protégé : entrez le <strong>code à 6 chiffres</strong> de votre app d'authentification, ou votre <strong>code de secours</strong>.")} path="login.2fa_hint" />
              </p>
              <div>
                <label className="block text-sm text-ivory/70 mb-2">Code de vérification</label>
                <input data-testid="login-2fa-code" type="text" inputMode="numeric" autoComplete="one-time-code"
                  required autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="123 456"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-center text-lg tracking-[0.4em] focus:border-gold focus:outline-none transition-colors duration-300" />
              </div>
              <button data-testid="login-2fa-submit" type="submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Vérifier et entrer
              </button>
              <button type="button" data-testid="login-2fa-back"
                onClick={() => { setStep("password"); setCode(""); setError(""); }}
                className="w-full text-sm text-ivory/60 hover:text-gold transition-colors">
                ← Retour à la connexion
              </button>
            </form>
          )}

          {/* Activation 2FA EXIGÉE (avis de 30 jours écoulé) : QR à scanner puis 1er code */}
          {step === "enroll" && (
            <form onSubmit={activate2fa} className="space-y-5" data-testid="login-2fa-enroll">
              <p className="text-sm text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-lg px-4 py-3">
                <RichText text={tx("login.2fa_enroll_hint", "La double vérification est maintenant <strong>obligatoire</strong> pour les administrateurs (avis donné 30 jours à l'avance). Scannez ce code avec <strong>Google Authenticator</strong> ou <strong>Microsoft Authenticator</strong> :")} path="login.2fa_enroll_hint" />
              </p>
              {enroll ? (
                <>
                  <img src={enroll.qr} alt="QR code double vérification" data-testid="login-2fa-qr"
                    className="mx-auto rounded-xl border border-gold/30 bg-white p-3 w-48 h-48" />
                  <p className="text-xs text-ivory/60 text-center">
                    Impossible de scanner ? Entrez ce code manuellement dans l'app :<br />
                    <code data-testid="login-2fa-secret" className="text-gold tracking-widest select-all">{enroll.secret}</code>
                  </p>
                </>
              ) : (
                <p className="text-center text-ivory/60 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></p>
              )}
              <div>
                <label className="block text-sm text-ivory/70 mb-2">Code affiché dans l'app</label>
                <input data-testid="login-2fa-enroll-code" type="text" inputMode="numeric" autoComplete="one-time-code"
                  required value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="123 456"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-center text-lg tracking-[0.4em] focus:border-gold focus:outline-none transition-colors duration-300" />
              </div>
              <button data-testid="login-2fa-activate" type="submit" disabled={loading || !enroll}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Activer et entrer
              </button>
            </form>
          )}

          {step === "password" && (<>
          <div className="flex items-center gap-4 my-6">
            <span className="flex-1 border-t border-ivory/10" />
            <span className="text-xs text-ivory/65 uppercase tracking-widest">{t("auth.or")}</span>
            <span className="flex-1 border-t border-ivory/10" />
          </div>
          <button
            type="button"
            data-testid="google-login"
            onClick={googleLogin}
            className="w-full inline-flex items-center justify-center gap-3 border border-ivory/25 text-ivory px-6 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <Chrome className="w-5 h-5" /> {t("auth.google")}
          </button>
          <p className="text-sm text-ivory/70 mt-6 text-center">
            {t("login.no_account")}{" "}
            <Link to="/inscription" state={location.state} data-testid="goto-register" className="text-gold hover:text-champagne transition-colors duration-300">{t("login.create")}</Link>
          </p>
          </>)}
        </div>
      </FadeUp>
    </div>
  );
}
